import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FileLibrary } from "@/features/files/FileLibrary";
import { fileOpenPath, filterFileEntries, sortFileEntries, type FileEntry } from "@/features/files/file-index";
import { isFileMockEntry } from "@/features/files/file-mock";
import { openFileInEditor } from "@/features/files/file-open";
import { useDisplayedFiles } from "@/features/files/use-file-entries";
import { useFileListParams } from "@/features/files/use-file-list";
import { useProjectStore, type SheetTab } from "@/store/project-store";

export function ProjectFilesPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { kind, source, sort, query, planId, patch, selectKind } = useFileListParams();
  const library = useDisplayedFiles(id);
  const visible = useMemo(
    () => sortFileEntries(filterFileEntries(library, { text: query, kind, source, projectId: planId }), sort),
    [library, query, kind, source, planId, sort],
  );

  const owner = params.get("owner");
  const ownerKind = params.get("ownerKind");
  useEffect(() => {
    const dropScope = params.has("scope");
    if (!dropScope && (!owner || (ownerKind !== "task" && ownerKind !== "record"))) return;
    const comment = params.get("comment") || undefined;
    if (owner && (ownerKind === "task" || ownerKind === "record")) {
      const tab: SheetTab = comment ? "comments" : "attachments";
      const store = useProjectStore.getState();
      if (ownerKind === "task") store.setExecutionFocus(owner, { tab, commentId: comment });
      else store.setRecordFocus(owner, { tab, commentId: comment });
    }
    const updated = new URLSearchParams(params);
    updated.delete("scope");
    updated.delete("owner");
    updated.delete("ownerKind");
    updated.delete("tab");
    updated.delete("comment");
    setParams(updated, { replace: true });
  }, [owner, ownerKind, params, setParams]);

  const open = (entry: FileEntry) => {
    if (isFileMockEntry(entry)) return;
    if (entry.ownerKind === "document" || entry.projectId !== id) {
      navigate(fileOpenPath(entry));
      return;
    }
    openFileInEditor(entry);
  };

  return (
    <FileLibrary
      library={library}
      entries={visible}
      scope="plan"
      kind={kind}
      source={source}
      query={query}
      planId={planId}
      sort={sort}
      onKind={selectKind}
      onSource={(next) => patch({ source: next === "all" ? null : next })}
      onQuery={(next) => patch({ q: next.trim() ? next : null })}
      onPlan={(next) => patch({ plan: next || null })}
      onSort={(next) => patch({ sort: next === "recent" ? null : next })}
      onOpen={open}
    />
  );
}
